import { Bell, BellOff } from "lucide-react";
import { useEffect, useState } from "react";

import {
  fetchAppointmentPushStatus,
  fetchOrderPushStatus,
  subscribeToAppointmentPush,
  subscribeToOrderPush,
  unsubscribeFromPush,
  type BrowserPushSubscription,
} from "@/features/storefront/api/storefront";
import { useShopContent } from "@/features/storefront/content/useContent";
import {
  currentSubscription,
  hasDeviceOptIn,
  pushPermission,
  setDeviceOptIn,
  subscribeBrowser,
  toServerShape,
  useStorefrontPush,
} from "@/features/storefront/pwa/push";
import { useStorefrontTheme } from "@/features/storefront/useStorefrontTheme";

type State = "checking" | "off" | "on" | "blocked" | "busy" | "failed";

/**
 * "Notify me when this order moves", on the confirmation page.
 *
 * Offered only where the shop offers push (see `useStorefrontPush`) and the
 * browser can do it, and only ever as a button: the browser's permission
 * prompt is asked from the shopper's own tap, never on page load, because a
 * prompt nobody asked for is the one they learn to dismiss.
 *
 * A guest follows the order with the token checkout returned; a signed-in
 * shopper's own order needs none. Either way the server decides what the
 * notification says - the shop's name and its order-update wording.
 */
export function OrderUpdatesPrompt({
  orderId,
  orderToken,
}: {
  orderId: string;
  orderToken?: string | null;
}) {
  return <FollowUpdatesPrompt kind="order" id={orderId} token={orderToken} />;
}

/**
 * The same offer on a booking's confirmation: "Get a reminder on this device
 * before your appointment". A yes there is a yes for the device too, so
 * later orders and bookings from it are followed without asking again.
 */
export function AppointmentUpdatesPrompt({
  appointmentId,
  appointmentToken,
}: {
  appointmentId: string;
  appointmentToken?: string | null;
}) {
  return (
    <FollowUpdatesPrompt
      kind="appointment"
      id={appointmentId}
      token={appointmentToken}
    />
  );
}

type Followable = "order" | "appointment";

const FOLLOW: Record<
  Followable,
  {
    follow: (
      subscription: BrowserPushSubscription,
      id: string,
      token: string | null | undefined,
    ) => Promise<unknown>;
    status: (endpoint: string, id: string) => Promise<{ following: boolean }>;
    prompt: string;
    on: string;
  }
> = {
  order: {
    follow: (subscription, orderId, orderToken) =>
      subscribeToOrderPush(subscription, { orderId, orderToken }),
    status: fetchOrderPushStatus,
    prompt: "order.push_prompt",
    on: "order.push_on",
  },
  appointment: {
    follow: (subscription, appointmentId, appointmentToken) =>
      subscribeToAppointmentPush(subscription, { appointmentId, appointmentToken }),
    status: fetchAppointmentPushStatus,
    prompt: "booking.push_prompt",
    on: "booking.push_on",
  },
};

function FollowUpdatesPrompt({
  kind,
  id,
  token,
}: {
  kind: Followable;
  id: string;
  token?: string | null;
}) {
  const follow = FOLLOW[kind];
  const push = useStorefrontPush();
  const theme = useStorefrontTheme();
  const copy = useShopContent();
  const [state, setState] = useState<State>("checking");

  useEffect(() => {
    if (!push) return;
    let cancelled = false;
    const settle = (next: State) => {
      if (!cancelled) setState(next);
    };
    const permission = pushPermission();
    if (permission === "denied") {
      settle("blocked");
    } else if (permission !== "granted") {
      settle("off");
    } else {
      // Already allowed: ask the server whether *this* order is followed, so a
      // reload says "on" rather than offering again. A device that already
      // said yes to order updates follows a new order without being asked:
      // the permission is granted, so nothing here prompts.
      const optedIn = hasDeviceOptIn(push);
      currentSubscription(push)
        .then(async (current) => {
          if (current) {
            const status = await follow.status(current.endpoint, id);
            if (status.following) return settle("on");
          }
          if (!optedIn) return settle("off");
          const subscription = current
            ? toServerShape(current)
            : await subscribeBrowser(push);
          if (!subscription) return settle("off");
          await follow.follow(subscription, id, token);
          settle("on");
        })
        .catch(() => settle("off"));
    }
    return () => {
      cancelled = true;
    };
  }, [push, id, token, follow]);

  if (!push || pushPermission() === "unsupported" || state === "checking")
    return null;

  const enable = async () => {
    setState("busy");
    try {
      const subscription = await subscribeBrowser(push);
      if (!subscription) {
        setState(pushPermission() === "denied" ? "blocked" : "off");
        return;
      }
      await follow.follow(subscription, id, token);
      // A yes here is a yes for this device: later orders are followed too.
      setDeviceOptIn(push, true);
      setState("on");
    } catch {
      setState("failed");
    }
  };

  const disable = async () => {
    setState("busy");
    try {
      const subscription = await currentSubscription(push);
      if (subscription) {
        await unsubscribeFromPush(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setDeviceOptIn(push, false);
      setState("off");
    } catch {
      setState("failed");
    }
  };

  const on = state === "on";
  return (
    <div
      className="mx-auto mt-8 flex max-w-xl flex-col items-center gap-3 border p-4 text-center text-sm sm:flex-row sm:text-left"
      style={{
        borderColor: theme?.colors.surface,
        borderRadius: "var(--storefront-radius)",
      }}
      aria-live="polite"
    >
      {on ? (
        <Bell
          className="size-5 shrink-0"
          style={{ color: theme?.colors.primary }}
          aria-hidden
        />
      ) : (
        <BellOff
          className="size-5 shrink-0"
          style={{ color: theme?.colors.muted }}
          aria-hidden
        />
      )}
      <p
        className="flex-1"
        style={{ color: state === "blocked" ? theme?.colors.muted : undefined }}
      >
        {on
          ? copy(follow.on)
          : state === "blocked"
            ? copy("order.push_blocked")
            : state === "failed"
              ? copy("order.push_failed")
              : copy(follow.prompt)}
      </p>
      {state === "blocked" ? null : on ? (
        <button
          type="button"
          onClick={() => void disable()}
          className="shrink-0 text-sm underline"
        >
          {copy("order.push_disable")}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void enable()}
          disabled={state === "busy"}
          className="inline-flex h-10 shrink-0 items-center px-5 text-sm font-semibold text-white disabled:opacity-60"
          style={{
            backgroundColor: theme?.colors.primary,
            borderRadius: "var(--storefront-radius)",
          }}
        >
          {copy("order.push_enable")}
        </button>
      )}
    </div>
  );
}
